import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { ETAPA_AO_CANCELAR, temOutraConsultaFutura, validarCancelamento } from '@/agenda/cancelamento';
import { dataDeRetornoPadrao } from '@/leads/retorno';
import { format } from 'date-fns';
import { buscarAgendamentosDoLead, cancelarAgendamento } from '@/services/agendamentosService';
import { tratarErro } from '@/lib/erros';

/**
 * Cancelar uma consulta pela agenda.
 *
 * ★ VQ-3 — se era a ÚLTIMA consulta futura do lead, o cancelamento MOVE o lead
 * para "Não compareceu" e exige a data do retorno (vira tarefa no mesmo lote).
 * Não há mais "Manter em Agendado": era a única forma de produzir lead em
 * Agendado sem consulta nenhuma. Se havia OUTRA consulta futura, a etapa não
 * muda e o diálogo não pergunta nada. O agendamento fica `cancelado` — nunca
 * `faltou` — e a timeline registra "Consulta cancelada".
 */
export default function DialogoCancelar({
  aberto,
  agendamento,
  clinicaId,
  autor,
  config = null,
  aoFechar,
  aoConcluir,
}) {
  const [motivo, setMotivo] = useState('');
  const [retorno, setRetorno] = useState(''); // '' = usa o padrão; null = apagado
  const [eraAUltima, setEraAUltima] = useState(null); // null = ainda consultando
  const [erro, setErro] = useState(null);
  // VQ-correção 1: falha ao conferir as outras consultas é ERRO, não decisão de negócio.
  const [erroConsulta, setErroConsulta] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!aberto || !agendamento) return undefined;
    setMotivo('');
    setRetorno('');
    setErro(null);
    setErroConsulta(null);
    setEraAUltima(null);
    if (!agendamento.leadId || !clinicaId) {
      setEraAUltima(false);
      return undefined;
    }
    let vivo = true;
    buscarAgendamentosDoLead(clinicaId, agendamento.leadId)
      .then((lista) => {
        if (vivo) setEraAUltima(!temOutraConsultaFutura(lista, agendamento.id));
      })
      // ★ ERRO NÃO DECIDE NEGÓCIO: sem saber se era a última, `eraAUltima` fica null
      // (botão desabilitado) e a pessoa vê o porquê. Em produção este catch escondeu
      // um índice ausente durante semanas (docs/vq-diagnostico.md).
      .catch((e) => { if (vivo) { tratarErro('as consultas do lead', null, 'service')(e); setErroConsulta('Não foi possível conferir as outras consultas deste lead. Tente de novo; se persistir, avise o suporte.'); } });
    return () => { vivo = false; };
  }, [aberto, agendamento, clinicaId]);

  const vaiVoltar = eraAUltima === true;
  const retornoPadrao = vaiVoltar ? format(dataDeRetornoPadrao(null, ETAPA_AO_CANCELAR, config), "yyyy-MM-dd'T'HH:mm") : '';
  const retornoAtual = retorno === null ? '' : (retorno || retornoPadrao);

  async function confirmar() {
    setErro(null);
    setOcupado(true);
    try {
      const retornoEm = vaiVoltar && retornoAtual ? new Date(retornoAtual) : null;
      const invalido = validarCancelamento({ voltarEtapa: vaiVoltar, retornoEm });
      if (invalido) throw new Error(invalido);
      await cancelarAgendamento(
        clinicaId,
        agendamento,
        { motivo, voltarEtapa: vaiVoltar, ...(retornoEm ? { retornoEm } : {}) },
        autor,
      );
      aoConcluir?.(agendamento);
      aoFechar?.();
    } catch (e) {
      setErro(e.message ?? 'Não foi possível cancelar a consulta.');
    } finally {
      setOcupado(false);
    }
  }

  if (!agendamento) return null;

  return (
    <Dialogo titulo="Cancelar consulta" aberto={aberto} aoFechar={aoFechar}>
      <div className="dialogo-corpo">
        <p className="campo-ajuda">
          {agendamento.leadNome || 'Sem nome'} — a vaga volta a ficar livre e outra pessoa pode
          ocupá-la. Não há como desfazer: para voltar atrás é preciso remarcar.
        </p>

        <div className="campo">
          <label htmlFor="cancelar-motivo">Motivo (opcional)</label>
          <input
            id="cancelar-motivo"
            value={motivo}
            placeholder="o paciente desmarcou"
            disabled={ocupado}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>

        {vaiVoltar && (
          <fieldset className="campo" id="cancelar-voltar">
            <legend>Esta era a única consulta futura deste lead.</legend>
            <p className="campo-ajuda">
              O lead vai para <strong>Não compareceu</strong> (a consulta fica registrada como
              cancelada, não como falta) e ganha um retorno em Próximos contatos.
            </p>
            <div className="campo">
              <label htmlFor="cancelar-retorno">Retorno em</label>
              <input id="cancelar-retorno" type="datetime-local" required value={retornoAtual} disabled={ocupado}
                onChange={(e) => setRetorno(e.target.value === '' ? null : e.target.value)} />
              <p className="campo-ajuda">Vira um próximo contato em Próximos contatos.</p>
            </div>
          </fieldset>
        )}

        {erroConsulta && <p className="erro" role="alert">{erroConsulta}</p>}
        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" disabled={ocupado} onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="btn-primario"
            disabled={ocupado || eraAUltima === null}
            onClick={confirmar}
          >
            {ocupado ? 'Cancelando…' : 'Cancelar a consulta'}
          </button>
        </footer>
      </div>
    </Dialogo>
  );
}
